import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mail, Globe, Calendar, User, Phone, Building } from 'lucide-react';
import type { Tenant } from '@/pages/admin/AdminTenantDetail';

interface TenantOverviewTabProps {
  tenant: Tenant;
}

const statusTimeline = [
  { key: 'onboarding', label: 'Onboarding', description: 'Client filling intake form' },
  { key: 'intake', label: 'Intake', description: 'Review submitted materials' },
  { key: 'building', label: 'Building', description: 'Bot configuration in progress' },
  { key: 'preview', label: 'Preview', description: 'Testing and approval' },
  { key: 'live', label: 'Live', description: 'Bot deployed to production' },
];

const TenantOverviewTab: React.FC<TenantOverviewTabProps> = ({ tenant }) => {
  const currentStatusIndex = statusTimeline.findIndex(s => s.key === tenant.status);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Status Timeline */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Status Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {statusTimeline.map((step, index) => {
              const isCompleted = index < currentStatusIndex;
              const isCurrent = index === currentStatusIndex;
              const isPending = index > currentStatusIndex;

              return (
                <div key={step.key} className="flex items-start gap-4 pb-6 last:pb-0">
                  <div className="relative flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </div>
                    {index < statusTimeline.length - 1 && (
                      <div
                        className={`w-0.5 h-full absolute top-8 ${
                          isCompleted ? 'bg-green-500' : 'bg-border'
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-h-[60px]">
                    <p
                      className={`font-medium ${
                        isPending ? 'text-muted-foreground' : 'text-foreground'
                      }`}
                    >
                      {step.label}
                    </p>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Info */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Building className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Industry</p>
              <p className="font-medium capitalize">{tenant.industry || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-medium">
                {tenant.created_at
                  ? new Date(tenant.created_at).toLocaleDateString()
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Client Contact</p>
                <p className="font-medium">{tenant.client_contact_email || '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Developer/IT Email</p>
                <p className="font-medium">{tenant.developer_email || '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Summary Forward Email</p>
                <p className="font-medium">{tenant.summary_forward_email || '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Install Scope</p>
                <p className="font-medium capitalize">{tenant.install_scope || '—'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Domains */}
      <Card>
        <CardHeader>
          <CardTitle>Domains</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Primary Domain</p>
              <p className="font-medium">{tenant.primary_domain || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Website URL</p>
              {tenant.website_url ? (
                <a 
                  href={tenant.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  {tenant.website_url}
                </a>
              ) : (
                <p className="font-medium">—</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Specialisations */}
      {tenant.specialisations && (
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Specialisations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(tenant.specialisations) 
                ? tenant.specialisations.map((spec: string, i: number) => (
                    <Badge key={i} variant="secondary">{spec}</Badge>
                  ))
                : typeof tenant.specialisations === 'object' && tenant.specialisations.selected
                ? tenant.specialisations.selected.map((spec: string, i: number) => (
                    <Badge key={i} variant="secondary">{spec}</Badge>
                  ))
                : <p className="text-muted-foreground">No specialisations defined</p>
              }
            </div>
            {tenant.specialisations?.notes && (
              <p className="text-sm text-muted-foreground mt-3">
                {tenant.specialisations.notes}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Do Not Mention */}
      {tenant.do_not_mention && (
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-destructive">Do Not Mention</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{tenant.do_not_mention}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenantOverviewTab;
